'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import AnimatedSection from '../ui/AnimatedSection';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';

interface SalonGalleryProps {
  className?: string;
}

const fallbackImages = [
  { id: '1', src: '/images/salon-showcase-1.webp', alt: 'Örnek Salon Görselimiz', description: 'Minimalist tasarım ve fonksiyonel mobilyalar.' },
  { id: '2', src: '/images/salon-showcase-2.webp', alt: 'Örnek Salon Görselimiz', description: 'Zarif detaylar ve konforlu oturma alanları.' },
  { id: '3', src: '/images/salon-showcase-3.webp', alt: 'Örnek Salon Görselimiz', description: 'Retro tarzı ve sıcak atmosfer.' },
  { id: '4', src: '/images/salon-showcase-4.webp', alt: 'Örnek Salon Görselimiz', description: 'Ham dokular ve metal aksesuarlar.' },
  { id: '5', src: '/images/showroom.webp', alt: 'Örnek Salon Görselimiz', description: 'Aydınlık ve ferah çalışma alanları.' },
  { id: '6', src: '/images/salon-showcase-1asd.webp', alt: 'Örnek Salon Görselimiz', description: 'Konforlu koltuklar ve maskülen tasarım.' },
];

type GalleryDisplayItem = {
  id: string;
  src: string;
  alt: string;
  description: string;
};

/* ------------------------------------------------------------------ */
/*  Grid placement map – asymmetric masonry                           */
/* ------------------------------------------------------------------ */
const gridPlacements: Record<number, string> = {
  0: 'md:col-span-2 md:row-span-2',   // large featured
  1: 'md:col-span-1 md:row-span-1',
  2: 'md:col-span-1 md:row-span-1',
  3: 'md:col-span-1 md:row-span-2',   // tall accent
  4: 'md:col-span-1 md:row-span-1',
  5: 'md:col-span-1 md:row-span-1',
};

const SalonGallery = ({ className }: SalonGalleryProps) => {
  const [images, setImages] = useState<GalleryDisplayItem[]>(fallbackImages);

  useEffect(() => {
    let isMounted = true;
    async function loadGallery() {
      try {
        const res = await fetch('/api/gallery?pageSize=6', { cache: 'no-store' });
        const data = await res.json();
        if (isMounted && data.success && Array.isArray(data.data) && data.data.length > 0) {
          const fetchedImages = data.data.slice(0, 6).map((item: any) => ({
            id: item.id,
            src: item.imageUrl,
            alt: item.title || 'Filiz Kozmetik Proje Görseli',
            description: item.description || 'Özel tasarım kuaför ve güzellik salonu projemiz.',
          }));

          // Eğer 6'dan az görsel geldiyse, eksik olanları fallbackImages ile tamamla
          if (fetchedImages.length < 6) {
            const remaining = 6 - fetchedImages.length;
            const combined = [...fetchedImages, ...fallbackImages.slice(0, remaining)];
            setImages(combined);
          } else {
            setImages(fetchedImages);
          }
        }
      } catch (error) {
        console.error('Galeri yüklenirken hata oluştu:', error);
      }
    }
    loadGallery();

    return () => { isMounted = false; };
  }, []);

  return (
    <section
      className={cn(
        'relative bg-gradient-to-b from-white to-stone-50/20 overflow-hidden',
        className,
      )}
    >
      {/* Ambient Red Spotlight Glows */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-primary/10 to-transparent rounded-tr-full opacity-50 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/5 to-transparent rounded-full opacity-30 pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20 md:py-28">
        {/* ── Section header ─────────────────────────────────────── */}
        <AnimatedSection animation="fadeIn">
          <div className="text-center max-w-3xl mx-auto mb-14">
            {/* Badge */}
            <span className="inline-flex items-center gap-2 rounded-full bg-red-50 px-4 py-1.5 text-sm font-semibold text-red-800 mb-5 ring-1 ring-red-100">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
              </span>
              Örnek Projelerimiz
            </span>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-4">
              Müşterilerimizden{' '}
              <span className="text-red-800">İlham Alın</span>
            </h2>

            <p className="text-gray-500 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
              Kurulumunu yaptığımız ve mobilyalarımızla hayat bulan birbirinden şık berber, kuaför ve güzellik salonları.
            </p>
          </div>
        </AnimatedSection>

        {/* ── Masonry grid ───────────────────────────────────────── */}
        <div
          className={cn(
            'grid gap-4 md:gap-5',
            // Mobile: single column
            'grid-cols-1',
            // Desktop: 3-col masonry with auto rows
            'md:grid-cols-3 md:auto-rows-[240px]',
          )}
        >
          {images.map((item, index) => (
            <AnimatedSection
              key={item.id}
              animation="fadeIn"
              delay={index * 80}
              className={cn(
                gridPlacements[index] ?? 'md:col-span-1 md:row-span-1',
              )}
            >
              <div
                className={cn(
                  'relative w-full h-72 md:h-full rounded-2xl overflow-hidden group cursor-pointer',
                  'ring-1 ring-black/5',
                  'transition-all duration-500 ease-out',
                  'hover:scale-[1.02] hover:shadow-2xl hover:ring-black/10',
                )}
              >
                {/* Image */}
                <Image
                  src={item.src}
                  alt={item.alt}
                  fill
                  className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-110"
                  sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
                  quality={75}
                />

                {/* Hover overlay – description only */}
                <div
                  className={cn(
                    'absolute inset-0 flex flex-col justify-end p-5 md:p-6',
                    'bg-gradient-to-t from-black/70 via-black/30 to-transparent',
                    'opacity-0 group-hover:opacity-100',
                    'transition-opacity duration-500 ease-out',
                  )}
                >
                  <p className="text-white text-sm sm:text-base font-medium leading-snug translate-y-3 group-hover:translate-y-0 transition-transform duration-500 ease-out drop-shadow-lg">
                    {item.description}
                  </p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* ── CTA button ─────────────────────────────────────────── */}
        <AnimatedSection animation="fadeIn" delay={500}>
          <div className="text-center mt-14 md:mt-16">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-full px-8 py-3 shadow-lg shadow-red-600/25 hover:shadow-xl hover:shadow-red-600/30 hover:from-red-700 hover:to-red-800 transition-all duration-300"
            >
              <Link href="/galeri" className="inline-flex items-center gap-2">
                Tüm Projeleri Gör
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default SalonGallery;